import {throttle} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import toast from '@/components/HotToast';
import {useEscpacePress} from '@/hooks';
import {AnalysisItem} from '@/map/@types';
import {MESSAGES, SHAPE_COLOR_OPTIONS} from '@/map/constants';
import {AreaOverlayType} from '@/map/enums';
import {Circle} from '@/map/modules/Overlay';
import {distanceToString} from '@/map/utils';
import {getIntlString} from '@/utils';
import {DrawInfoCursor, DrawStartCursor} from '../MapCursor';
import {CircleWithCenterPoint} from '../Overlay';

interface IProps {
  map: naver.maps.Map;
  item: AnalysisItem[AreaOverlayType.CIRCLE];
  isActiveZoomLevel?: boolean;
  onCancel?(): void;
  onComplete?(item: AnalysisItem[AreaOverlayType.CIRCLE]): void;
}

const MAX_RADIUS = 3 * 1000; // 3km

const colors = SHAPE_COLOR_OPTIONS[AreaOverlayType.CIRCLE];

// 반경 분석 위한 거리는 쿼리에 사용되므로 정수형 (반올림)으로 처리
export const CircleDrawingLayer = ({
  map,
  item,
  isActiveZoomLevel,
  onCancel,
  onComplete,
}: IProps) => {
  const evt = useRef<naver.maps.MapEventListener[]>([]);
  const [center, setCenter] = useState<naver.maps.LatLng | undefined>(
    item?.center as naver.maps.LatLng
  );
  const [guidePoint, setGuidePoint] = useState<naver.maps.LatLng>();
  const [radius, setRadius] = useState<number | undefined>(item?.radius);
  const [endpoint, setEndpoint] = useState<naver.maps.LatLng>();

  const getRadius = (a: naver.maps.LatLng, b: naver.maps.LatLng) => {
    const proj = map.getProjection();
    const distance = Math.round(proj.getDistance(a, b));

    return Math.min(distance, MAX_RADIUS);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMousemoveEvent = useCallback(
    throttle((e: {coord: naver.maps.LatLng}) => {
      if (!map) {
        return;
      }

      let coord = e.coord;

      if (center) {
        const proj = map.getProjection();
        const distance = Math.round(proj.getDistance(center, coord));

        if (distance > MAX_RADIUS) {
          const pt1 = proj.fromCoordToPoint(center);
          const pt2 = proj.fromCoordToPoint(coord);
          const dx = pt2.x - pt1.x;
          const dy = pt2.y - pt1.y;
          const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;

          coord = proj.getDestinationCoord(
            center,
            angle,
            MAX_RADIUS
          ) as naver.maps.LatLng;
        }
      }

      setGuidePoint(coord);
    }, 50),
    [center, map]
  );

  const onEscapePress = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  useEscpacePress(onEscapePress);

  useEffect(() => {
    if (!isActiveZoomLevel) {
      toast(
        getIntlString(MESSAGES, 'area-analytics-zoom-warning', {
          type: '반경 분석을',
        })
      );
    }
  }, [isActiveZoomLevel]);

  useEffect(() => {
    if (map && center && guidePoint) {
      const distance = getRadius(center, guidePoint);

      setRadius(distance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidePoint]);

  useEffect(() => {
    if (!center) {
      return;
    } else if (!isActiveZoomLevel) {
      setCenter(undefined);
      return;
    }

    setGuidePoint(center);

    evt.current.push(
      naver.maps.Event.addListener(map, 'mousemove', onMousemoveEvent)
    );

    evt.current.push(
      naver.maps.Event.addListener(
        map,
        'mousedown',
        (e: {coord: naver.maps.LatLng}) => setEndpoint(e.coord)
      )
    );

    return () => {
      if (evt?.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        evt.current.map((event) => naver.maps.Event.removeListener(event));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, center]);

  useEffect(() => {
    if (!map || !radius || center) {
      return;
    }
    // 고정 반경 분석용 코드
    // 추후 활용 시 zoomLevel에 따른 disable 처리 필요

    evt.current.push(
      naver.maps.Event.addListener(map, 'mousemove', onMousemoveEvent)
    );

    evt.current.push(
      naver.maps.Event.addListener(
        map,
        'mousedown',
        (e: {coord: naver.maps.LatLng}) =>
          onComplete?.({
            center: e.coord,
            radius,
          })
      )
    );

    return () => {
      if (evt?.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        evt.current.map((event) => naver.maps.Event.removeListener(event));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, radius]);

  useEffect(() => {
    if (center && endpoint) {
      if (center.equals(endpoint)) {
        return;
      }

      if (isActiveZoomLevel) {
        onComplete?.({
          center,
          radius: getRadius(center, endpoint),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  if (center) {
    return (
      <>
        {!!guidePoint && (
          <DrawInfoCursor
            map={map}
            position={guidePoint}
            hint="분석"
            typeText="반경"
            valueText={distanceToString(getRadius(center, guidePoint), {
              roundingMode: 'ceil',
            })}
            menu={{선택: '클릭', 취소: 'Esc'}}
            color={colors.strokeColor}
            onEscKeyPress={onEscapePress}
          />
        )}
        {!!radius && (
          <CircleWithCenterPoint
            map={map}
            center={center}
            radius={radius}
            {...colors}
          />
        )}
      </>
    );
  } else {
    return (
      <>
        {radius && guidePoint && (
          <Circle map={map} center={guidePoint} radius={radius} {...colors} />
        )}
        <DrawStartCursor
          map={map}
          hint={
            radius
              ? `${distanceToString(radius, {roundingMode: 'ceil'})} 분석`
              : '분석'
          }
          color={colors.strokeColor}
          onPointed={radius ? undefined : (point) => setCenter(point)}
        />
      </>
    );
  }
};
